'use client'

/**
 * Login Button Component
 *
 * Header component that shows:
 * - "Sign In" text link + "Get Started" CTA when not logged in
 * - User avatar with dropdown menu when logged in
 */

import { useEffect, useState, useMemo } from 'react'
import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'
import { useAuth, getAuthHint } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { trackEvent } from '@/lib/analytics'
import {
  User,
  LayoutDashboard,
  CreditCard,
  Settings,
  LogOut,
  Loader2,
  Crown,
} from 'lucide-react'

interface LoginButtonProps {
  className?: string
  variant?: 'default' | 'mobile' | 'compact'
  /**
   * Visual tone for the signed-out "Sign In" control on the `default` variant.
   * `glass` swaps the green-outline pill for a translucent-white glass pill so it
   * sits on the frosted header pill (3a). Only affects the default variant's
   * signed-out + loading button; avatar/dropdown states are unchanged. The outer
   * `className` can't reach these inner button classes, so this prop is the hook.
   */
  tone?: 'default' | 'glass'
}

export function LoginButton({ className = '', variant = 'default', tone = 'default' }: LoginButtonProps) {
  const { user, loading, initialized, isPremium, signOut } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const [signingOut, setSigningOut] = useState(false)

  // Build login URL that redirects back to the current page after OAuth
  const loginHref = useMemo(() => {
    if (!pathname || pathname === '/auth/login') return '/auth/login'
    if (typeof window === 'undefined') return `/auth/login?redirect=${encodeURIComponent(pathname)}`

    const query = window.location.search
    const hash = window.location.hash
    const returnTo = `${pathname}${query}${hash}`
    return `/auth/login?redirect=${encodeURIComponent(returnTo)}`
  }, [pathname])

  // Same destination as loginHref — /auth/login is a unified OAuth page that
  // already reads "Sign in or create account", so there is no separate signup
  // route to point at. The param exists so the entry point is attributable and
  // so the page can lead with create-account framing without another link.
  const signupHref = useMemo(
    () => `${loginHref}${loginHref.includes('?') ? '&' : '?'}intent=signup`,
    [loginHref],
  )

  const handleSignOut = async () => {
    setSigningOut(true)
    try {
      await signOut()
      router.push('/')
    } catch (error) {
      console.error('Error signing out:', error)
    } finally {
      setSigningOut(false)
    }
  }

  // Read auth hint from localStorage to show appropriate skeleton shape.
  // Deferred to a post-mount effect so SSR and the first client render agree
  // (localStorage is unavailable on the server, so the hint must start null).
  const [authHint, setAuthHint] = useState<'logged_in' | 'logged_out' | null>(null)
  useEffect(() => {
    setAuthHint(getAuthHint())
  }, [])
  const likelyLoggedIn = authHint === 'logged_in'

  // Glass "Sign In" pill for the default variant on the frosted header (3a).
  // tailwind-merge (via the Button's cn) lets these win over the outline base.
  const glassSignIn =
    'border border-white/70 bg-white/55 text-green-800 backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] hover:bg-white/80 hover:text-green-900'

  // Show loading state - render a placeholder that matches the expected final state
  if (!initialized || loading) {
    if (variant === 'compact') {
      // If we have a hint the user is logged in, show the avatar circle;
      // otherwise render the real Sign In link styling so the text is in
      // the SSR HTML and auth resolution doesn't cause a visible swap.
      if (likelyLoggedIn) {
        return (
          <div className={`min-h-[44px] flex items-center ${className}`}>
            <div className="h-8 w-8 bg-gray-100 rounded-full animate-pulse" />
          </div>
        )
      }
      return (
        <span
          aria-busy="true"
          className={`text-green-700 text-sm font-semibold min-h-[44px] flex items-center cursor-default select-none ${className}`}
        >
          Sign In
        </span>
      )
    }
    if (variant === 'mobile') {
      // Mobile: avatar skeleton if likely logged in, otherwise render the
      // real Sign In button styling (disabled) so auth resolution is invisible.
      if (likelyLoggedIn) {
        return (
          <div className={`flex flex-col gap-2 ${className}`}>
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="h-10 w-10 bg-gray-100 rounded-full animate-pulse" />
              <div className="flex-1">
                <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>
          </div>
        )
      }
      return (
        <div className={className}>
          <Button
            disabled
            className="w-full justify-center bg-linear-to-r from-green-600 to-emerald-600 text-white shadow-md cursor-default select-none disabled:opacity-100"
          >
            Sign In
          </Button>
        </div>
      )
    }
    // Default variant: show avatar skeleton if likely logged in, otherwise
    // render the real Sign In button styling (disabled) so there's no visible
    // swap when auth resolves to !user (the common case on the homepage).
    if (likelyLoggedIn) {
      return (
        <div className={className}>
          <div className="h-10 w-10 bg-gray-100 rounded-full animate-pulse" />
        </div>
      )
    }
    return (
      <div className={className}>
        <Button
          size="sm"
          variant="outline"
          disabled
          className={`${
            tone === 'glass' ? glassSignIn : 'border-green-200 text-green-700'
          } px-5 py-2 rounded-full font-medium cursor-default select-none disabled:opacity-100`}
        >
          Sign In
        </Button>
      </div>
    )
  }

  // Show sign in + get started for unauthenticated users
  if (!user) {
    if (variant === 'mobile') {
      // Two-up at the top of the nav drawer. Sign up carries the emphasis
      // because new visitors dominate that surface; both min-h-11 to stay
      // above the 44px tap target when the row splits the drawer width.
      return (
        <div className={`flex items-center gap-2 ${className}`}>
          <Button
            asChild
            variant="outline"
            className="min-h-11 flex-1 justify-center border-green-200 bg-white/70 font-semibold text-green-800 hover:bg-white hover:text-green-900"
          >
            <Link
              href={loginHref}
              prefetch={false}
              onClick={() =>
                trackEvent('cta_clicked', {
                  cta_location: 'mobile_menu_sign_in',
                  cta_text: 'Sign in',
                  destination: 'auth_login',
                  intent: 'signin',
                })
              }
            >
              Sign in
            </Link>
          </Button>
          <Button
            asChild
            className="min-h-11 flex-1 justify-center bg-linear-to-r from-green-600 to-emerald-600 font-semibold text-white shadow-md hover:from-green-700 hover:to-emerald-700"
          >
            <Link
              href={signupHref}
              prefetch={false}
              onClick={() =>
                trackEvent('cta_clicked', {
                  cta_location: 'mobile_menu_sign_up',
                  cta_text: 'Sign up',
                  destination: 'auth_login',
                  intent: 'signup',
                })
              }
            >
              Sign up
            </Link>
          </Button>
        </div>
      )
    }

    if (variant === 'compact') {
      return (
        <Link
          href={loginHref}
          prefetch={false}
          className={`text-green-700 hover:text-green-800 text-sm font-semibold transition-colors min-h-[44px] flex items-center ${className}`}
        >
          Sign In
        </Link>
      )
    }

    return (
      <Button
        asChild
        size="sm"
        variant="outline"
        className={`${
          tone === 'glass'
            ? glassSignIn
            : 'border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800'
        } px-5 py-2 rounded-full font-medium transition-all duration-300`}
      >
        <Link href={loginHref} prefetch={false} className={className}>
          Sign In
        </Link>
      </Button>
    )
  }

  // Show user dropdown for authenticated users
  const initials = user.user_metadata?.full_name
    ?.split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || user.email?.[0].toUpperCase() || 'U'

  // Compact variant: small avatar with dropdown menu (used in mobile header)
  if (variant === 'compact') {
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            aria-label="Open account menu"
            className={`relative h-10 w-10 rounded-full p-0 hover:ring-2 hover:ring-green-200 transition-all ${className}`}
          >
            <Avatar className="h-8 w-8 ring-2 ring-green-100">
              <AvatarImage
                src={user.user_metadata?.avatar_url}
                alt={`${user.user_metadata?.full_name || user.email || 'User'} profile picture`}
              />
              <AvatarFallback className="bg-green-100 text-green-700 text-xs font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>
            {isPremium && (
              <span
                className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 bg-linear-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center ring-1 ring-white"
                aria-label="Premium member"
              >
                <Crown className="h-2 w-2 text-white" aria-hidden="true" />
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-52" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium leading-none truncate">
                {user.user_metadata?.full_name || 'User'}
              </p>
              {isPremium && (
                <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
                  Premium
                </Badge>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href="/dashboard" className="cursor-pointer">
              <LayoutDashboard className="mr-2 h-4 w-4" />
              Dashboard
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/dashboard/billing" className="cursor-pointer">
              <CreditCard className="mr-2 h-4 w-4" />
              Billing
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/dashboard/settings" className="cursor-pointer">
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleSignOut}
            disabled={signingOut}
            className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
          >
            {signingOut ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="mr-2 h-4 w-4" />
            )}
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  if (variant === 'mobile') {
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-10 w-10">
            <AvatarImage
              src={user.user_metadata?.avatar_url}
              alt={`${user.user_metadata?.full_name || user.email || 'User'} profile picture`}
            />
            <AvatarFallback className="bg-green-100 text-green-700">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user.user_metadata?.full_name || 'User'}
            </p>
            {/* Only show email if user doesn't have a name */}
            {!user.user_metadata?.full_name && user.email && (
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            )}
          </div>
          {isPremium && (
            <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
              Premium
            </Badge>
          )}
        </div>
        <Button asChild variant="outline" className="w-full min-h-[44px] justify-start">
          <Link href="/dashboard">
            <LayoutDashboard className="mr-2 h-4 w-4" />
            Dashboard
          </Link>
        </Button>
        <Button asChild variant="outline" className="w-full min-h-[44px] justify-start">
          <Link href="/dashboard/billing">
            <CreditCard className="mr-2 h-4 w-4" />
            Billing
          </Link>
        </Button>
        <Button asChild variant="outline" className="w-full min-h-[44px] justify-start">
          <Link href="/dashboard/settings">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </Link>
        </Button>
        <Button
          variant="ghost"
          onClick={handleSignOut}
          disabled={signingOut}
          className="w-full min-h-[44px] justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          {signingOut ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <LogOut className="mr-2 h-4 w-4" />
          )}
          Sign out
        </Button>
      </div>
    )
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label="Open account menu"
          className={`relative h-10 w-10 rounded-full hover:ring-2 hover:ring-green-200 transition-all ${className}`}
        >
          <Avatar className="h-9 w-9">
            <AvatarImage
              src={user.user_metadata?.avatar_url}
              alt={`${user.user_metadata?.full_name || user.email || 'User'} profile picture`}
            />
            <AvatarFallback className="bg-green-100 text-green-700 font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          {isPremium && (
            <span
              className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-linear-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center ring-2 ring-white"
              aria-label="Premium member"
            >
              <Crown className="h-2.5 w-2.5 text-white" aria-hidden="true" />
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium leading-none">
                {user.user_metadata?.full_name || 'User'}
              </p>
              {isPremium && (
                <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
                  Premium
                </Badge>
              )}
            </div>
            {/* Only show email if user doesn't have a name (avoids ugly Apple privacy relay emails) */}
            {!user.user_metadata?.full_name && user.email && (
              <p className="text-xs leading-none text-muted-foreground truncate">
                {user.email}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/dashboard" className="cursor-pointer">
            <LayoutDashboard className="mr-2 h-4 w-4" />
            Dashboard
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/dashboard/billing" className="cursor-pointer">
            <CreditCard className="mr-2 h-4 w-4" />
            Billing
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/dashboard/settings" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          disabled={signingOut}
          className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
        >
          {signingOut ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <LogOut className="mr-2 h-4 w-4" />
          )}
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
